/**
 * Dashboard - 대시보드 페이지
 *
 * Task 3.1: 계좌 요약 UI
 * Task 3.2: 포지션 목록 UI
 * Task 3.3: 주문 UI
 * Task 3.4: 거래 기록 UI
 */
import React from 'react';
import AccountSummary from '../components/AccountSummary';
import PositionsList from '../components/PositionsList';
import OrderForm from '../components/OrderForm';
import TradeHistory from '../components/TradeHistory';
import TradingModeBadge from '../components/TradingModeBadge';
import CrossMarketSnapshot from '../components/market/CrossMarketSnapshot';
import KrReplayPanel from '../components/market/KrReplayPanel';
import MarketScopeTabs from '../components/market/MarketScopeTabs';
import { useCrossMarketStatus } from '../hooks/useCrossMarketStatus';
import type { MarketScope } from '../types/marketScope';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [scope, setScope] = React.useState<MarketScope>('ALL');
  const marketStatus = useCrossMarketStatus();
  const showUs = scope !== 'KR';
  const showKr = scope !== 'US';

  return (
    <div className="dashboard">
      <DashboardHeader />
      <div className="dashboard-content">
        <MarketScopeTabs value={scope} onChange={setScope} status={marketStatus.data} />
        <CrossMarketSnapshot
          status={marketStatus.data}
          scope={scope}
          loading={marketStatus.loading}
          error={marketStatus.error}
        />

        {showUs && <UsDashboardPanels status={marketStatus.data} />}
        {showKr && <KrReplayPanel kr={marketStatus.data?.markets.KR} title="국내 시장 대시보드" />}
      </div>
    </div>
  );
};

function DashboardHeader() {
  return (
    <div className="dashboard-header">
      <div>
        <span className="dashboard-kicker">Portfolio Overview</span>
        <h1>대시보드</h1>
        <h2 className="dashboard-section-title">포트폴리오 현황</h2>
        <p className="subtitle">포트폴리오 현황, 보유 포지션, 최근 실현 손익 요약</p>
      </div>
      <TradingModeBadge className="dashboard-mode" showCaption />
    </div>
  );
}

function UsDashboardPanels({ status }: { status: ReturnType<typeof useCrossMarketStatus>['data'] }) {
  const handleOrderSuccess = () => window.location.reload();
  const isTossLive = status?.safety.trading_mode === 'live'
    && status.safety.live_trading_enabled
    && status.safety.toss_live_market === 'US'
    && status.safety.toss_us_live_enabled;
  return (
    <>
      <section className="dashboard-hero"><AccountSummary /></section>
      <section className="dashboard-grid">
        <div className="dashboard-main-panel"><PositionsList /></div>
        <aside className="dashboard-side-panel">
          <OrderForm onOrderSuccess={handleOrderSuccess} />
        </aside>
      </section>
      <section className="dashboard-section-note" aria-label="매매 이력 안내">
        <span>US</span>
        {isTossLive
          ? 'Toss US live_pilot 기준입니다. 첫 live 매도 체결 전까지 실현 손익은 데이터 없음으로 표시합니다.'
          : 'Toss US 주문 잠금 상태입니다. 실현 손익은 현재 표시 가능한 US 기록만 조회합니다.'}
      </section>
      <section className="dashboard-history-panel">
        <TradeHistory
          market="US"
          scope={isTossLive ? 'live' : 'paper'}
          title={isTossLive ? 'US Live 실현 손익 기록' : 'US 실현 손익 기록'}
          emptyMessage={isTossLive ? 'US live 실현 손익 데이터 없음' : '데이터 없음'}
        />
      </section>
    </>
  );
}

export default Dashboard;
